import moment from 'moment'

/**
 * api-pms-receiver reads telemetry timestamps back from MySQL with DateTimeKind.Unspecified, so they
 * serialize as UTC wall-clock strings without an offset (e.g. "2026-06-04T10:00:00"). Parsing those
 * with plain moment() uses the viewer timezone, which shifts every instant by the viewer's offset:
 * the timeline filter, log counts and rendered times then differ between viewers. Normalizing to a
 * canonical UTC ISO string here makes every downstream moment(...) call viewer-independent.
 */

const TIMELINE_EVENT_FIELDS = ['occurredAt', 'OccurredAt']
const ACTIVITY_WINDOW_FIELDS = ['startAt', 'StartAt', 'endAt', 'EndAt']
const DEVICE_FIELDS = [
  'lastSeenAt',
  'LastSeenAt',
  'firstLogAt',
  'FirstLogAt',
  'firstSeenAt',
  'FirstSeenAt',
  'lastSeenOn',
  'LastSeenOn',
  'lastClientEventOccurredAt',
  'LastClientEventOccurredAt'
]
const TELEMETRY_USER_FIELDS = ['lastActivityAt', 'LastActivityAt']

/**
 * Parses a value as UTC and re-emits a canonical UTC ISO string. Empty/invalid values pass through.
 * @param {string | null | undefined} value
 * @returns {string | null | undefined}
 */
export function toUtcIsoInstant(value) {
  if (value == null || value === '') {
    return value
  }
  const parsed = moment.utc(value)
  return parsed.isValid() ? parsed.toISOString() : value
}

function normalizeInstantFields(obj, fieldNames) {
  if (!obj || typeof obj !== 'object') {
    return obj
  }
  let result = obj
  for (const key of fieldNames) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) {
      continue
    }
    const normalized = toUtcIsoInstant(obj[key])
    if (normalized !== obj[key]) {
      if (result === obj) {
        result = { ...obj }
      }
      result[key] = normalized
    }
  }
  return result
}

export function normalizeTimelineEventInstants(events) {
  return (events || []).map(e => normalizeInstantFields(e, TIMELINE_EVENT_FIELDS))
}

export function normalizeActivityWindowInstants(windows) {
  return (windows || []).map(w => normalizeInstantFields(w, ACTIVITY_WINDOW_FIELDS))
}

export function normalizeDeviceInstants(devices) {
  return (devices || []).map(d => normalizeInstantFields(d, DEVICE_FIELDS))
}

export function normalizeTelemetryUserInstants(users) {
  return (users || []).map(u => normalizeInstantFields(u, TELEMETRY_USER_FIELDS))
}
